import React, { useEffect, useState, useMemo } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  PointElement,
} from "chart.js";
import styled from "styled-components";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";
import ReactSelect from "react-select";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

// === Styled components ===
const ChartWrapper = styled.div`
  background-color: #f9f9f9;
  padding: 20px;
`;

const ChartTitle = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-weight: bold;
  color: #666;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const InfoIconWrapper = styled.div`
  cursor: pointer;
  color: #666;
  transition: color 0.3s;
  &:hover { color: #000; }
  svg { width: 18px; height: 18px; }
`;

const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  margin-bottom: 16px;
`;

// react-select light styling to blend in
const selectStyles = {
  container: (base) => ({ ...base, minWidth: 260 }),
  menu: (base) => ({ ...base, zIndex: 5 }),
  control: (base) => ({
    ...base,
    borderRadius: 8,
    minHeight: 40,
    borderColor: "#ccc",
    boxShadow: "none",
    ":hover": { borderColor: "#aaa" },
    backgroundColor: "#fff",
  }),
  valueContainer: (base) => ({ ...base, padding: "2px 8px" }),
  input: (base) => ({ ...base, margin: 0 }),
  placeholder: (base) => ({ ...base, color: "#777" }),
};

const formatNumber = (value) =>
  Number(value || 0).toLocaleString("pl-PL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const GREEN = "#2e7d32";     // sales bars
const ORANGE = "#fb8c00";    // transactions bars
const BLUE = "#2196f3";      // avg line

const months = [
  { value: 0, label: "Cały rok" },
  { value: 1, label: "Styczeń" },
  { value: 2, label: "Luty" },
  { value: 3, label: "Marzec" },
  { value: 4, label: "Kwiecień" },
  { value: 5, label: "Maj" },
  { value: 6, label: "Czerwiec" },
  { value: 7, label: "Lipiec" },
  { value: 8, label: "Sierpień" },
  { value: 9, label: "Wrzesień" },
  { value: 10, label: "Październik" },
  { value: 11, label: "Listopad" },
  { value: 12, label: "Grudzień" },
];

// Safely compute one transaction value from row
const getRowValue = (row) =>
  parseFloat(row.K_15) ||
  parseFloat(row.K_19) ||
  parseFloat(row.K_10) ||
  parseFloat(row.K_21) ||
  parseFloat(row.K_17) ||
  parseFloat(row.K_11) ||
  parseFloat(row.K_22) ||
  0;

const ContractorsComparisonChart = ({ data = [] }) => {
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(0);

  const [contractors, setContractors] = useState([]);

  const [selectedContractors, setSelectedContractors] = useState({
    contractor1: "",
    contractor2: "",
    contractor3: "",
  });

  const filteredData = useMemo(() => {
    if (!data || data.length === 0 || !selectedYear) return [];
    return data.filter((row) => {
      const d = new Date(row.DataWystawienia);
      const y = !isNaN(d) ? d.getFullYear() : parseInt(row.Rok);
      const m = !isNaN(d) ? d.getMonth() + 1 : parseInt(row.Miesiac);
      if (String(y) !== String(selectedYear)) return false;
      if (selectedMonth !== 0 && m !== selectedMonth) return false;
      return true;
    });
  }, [data, selectedYear, selectedMonth]);

  // Populate year options & defaults
  useEffect(() => {
    if (!data || data.length === 0) return;
    const extractedYears = [
      ...new Set(
        data.map((row) => {
          const d = new Date(row.DataWystawienia);
          return !isNaN(d) ? d.getFullYear() : parseInt(row.Rok, 10);
        })
      ),
    ]
      .filter((y) => !!y)
      .sort((a, b) => a - b);

    setYears(extractedYears);
    if (!selectedYear && extractedYears.length) {
      setSelectedYear(String(extractedYears[extractedYears.length - 1]));
    }
  }, [data, selectedYear]);

  // Aggregate totals per contractor for list + defaults
  useEffect(() => {
    if (!filteredData.length) {
      setContractors([]);
      return;
    }

    const aggregated = filteredData.reduce((acc, row) => {
      const name = row.NazwaKontrahenta || "Nieznany kontrahent";
      const value = getRowValue(row);
      acc[name] = (acc[name] || 0) + value;
      return acc;
    }, {});

    const sortedNames = Object.entries(aggregated)
      .sort(([, a], [, b]) => b - a)
      .map(([name]) => name);

    setContractors(sortedNames);

    // Default three distinct top names if not set yet
    setSelectedContractors((prev) => ({
      contractor1: prev.contractor1 || sortedNames[0] || "",
      contractor2: prev.contractor2 || sortedNames[1] || "",
      contractor3: prev.contractor3 || sortedNames[2] || "",
    }));
  }, [filteredData]);

  const chartData = useMemo(() => {
    const selected = Object.values(selectedContractors).filter(Boolean);
    if (!selected.length || !filteredData.length) return null;

    const agg = filteredData.reduce((acc, row) => {
      const name = row.NazwaKontrahenta || "Nieznany kontrahent";
      if (!selected.includes(name)) return acc;

      const value = getRowValue(row);
      if (!acc[name]) acc[name] = { totalSales: 0, transactions: 0 };
      acc[name].totalSales += value;
      acc[name].transactions += 1;
      return acc;
    }, {});

    const labels = Object.keys(agg);
    const totals = labels.map((c) => agg[c].totalSales);
    const txCounts = labels.map((c) => agg[c].transactions);
    const avg = labels.map((c) =>
      agg[c].transactions > 0 ? agg[c].totalSales / agg[c].transactions : 0
    );

    return {
      labels,
      datasets: [
        {
          label: "Całkowita sprzedaż",
          data: totals,
          backgroundColor: GREEN,
          borderColor: GREEN,
          borderWidth: 1,
          yAxisID: "y",
          type: "bar",
        },
        {
          label: "Liczba transakcji",
          data: txCounts,
          backgroundColor: ORANGE,
          borderColor: ORANGE,
          borderWidth: 1,
          yAxisID: "y1",
          type: "bar",
        },
        {
          label: "Średnia wartość transakcji",
          data: avg,
          type: "line",
          borderColor: BLUE,
          backgroundColor: "rgba(33,150,243,0.12)",
          yAxisID: "y2",
          tension: 0.35,
          borderWidth: 2,
          pointRadius: 3,
          fill: true,
        },
      ],
    };
  }, [filteredData, selectedContractors]);

  const handleContractorChange = (key, option) => {
    setSelectedContractors((prev) => ({ ...prev, [key]: option?.value || "" }));
  };

  const contractorOptions = useMemo(
    () => contractors.map((c) => ({ value: c, label: c })),
    [contractors]
  );

  const selectedOption = (key) => {
    const val = selectedContractors[key];
    return val ? { value: val, label: val } : null;
  };

  return (
    <ChartWrapper>
      <ChartTitle>
        Porównanie sprzedaży oraz liczby transakcji dla kontrahentów
        <InfoIconWrapper data-tooltip-id="contractors-tooltip">
          <Info />
        </InfoIconWrapper>
      </ChartTitle>
      <InfoTooltip
        id="contractors-tooltip"
        place="right"
        style={{ fontSize: "12px", fontFamily: "Arial, sans-serif", color: "#fff" }}
        content={
          <>
            <strong>Porównanie kontrahentów</strong><br/>
            Filtruj po roku i miesiącu. Każda lista kontrahentów ma wyszukiwarkę wewnątrz rozwijanej listy.<br/>
            Zielone słupki – sprzedaż, pomarańczowe – liczba transakcji, niebieska linia – średnia wartość.
          </>
        }
      />

      {/* Global filters */}
      <FilterBar>
        <select
          value={selectedYear || ""}
          onChange={(e) => setSelectedYear(e.target.value)}
          style={{ padding: 8, borderRadius: 8, border: "1px solid #ccc", background: "#fff" }}
        >
          {years.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>

        <select
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(parseInt(e.target.value, 10))}
          style={{ padding: 8, borderRadius: 8, border: "1px solid #ccc", background: "#fff" }}
        >
          {months.map((m) => (
            <option key={m.value} value={m.value}>{m.label}</option>
          ))}
        </select>
      </FilterBar>

      {/* Contractor pickers — each with inline search in the dropdown */}
      <FilterBar>
        {["contractor1", "contractor2", "contractor3"].map((key, i) => (
          <ReactSelect
            key={key}
            styles={selectStyles}
            options={contractorOptions}
            value={selectedOption(key)}
            onChange={(opt) => handleContractorChange(key, opt)}
            isSearchable
            placeholder={`Wybierz kontrahenta (${i + 1})`}
            noOptionsMessage={() => "Brak wyników"}
          />
        ))}
      </FilterBar>

      {chartData ? (
        <Bar
          data={chartData}
          options={{
            responsive: true,
            plugins: {
              legend: { position: "bottom" },
              tooltip: {
                backgroundColor: "#333",
                titleColor: "#fff",
                bodyColor: "#fff",
                callbacks: {
                  label: (context) => {
                    const { dataset, parsed } = context;
                    return `${dataset.label}: ${formatNumber(parsed.y)}`;
                  },
                },
              },
            },
            scales: {
              x: { title: { display: true, text: "Kontrahenci" } },
              y: {
                type: "linear",
                position: "left",
                title: { display: true, text: "Sprzedaż" },
                ticks: { callback: (v) => formatNumber(v) },
              },
              y1: {
                type: "linear",
                position: "right",
                grid: { drawOnChartArea: false },
                title: { display: true, text: "Liczba transakcji" },
                ticks: { callback: (v) => formatNumber(v) },
              },
              y2: {
                type: "linear",
                position: "right",
                offset: true,
                grid: { drawOnChartArea: false },
                title: { display: true, text: "Średnia wartość" },
                ticks: { callback: (v) => formatNumber(v) },
              },
            },
          }}
        />
      ) : (
        <p style={{ textAlign: "center" }}>
          Brak danych dla wybranych filtrów lub wybierz kontrahentów z listy.
        </p>
      )}
    </ChartWrapper>
  );
};

export default ContractorsComparisonChart;
